import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, HelpCircle, MessageCircle, ChevronDown } from "lucide-react";
import { usePageTitle } from "../hooks/usePageTitle";
import { trackWhatsApp } from "../lib/beacon";

const SUPPORT_WA = (process.env.REACT_APP_SUPPORT_WA || "+5511996420785").replace(/[^0-9]/g, "");
const WA_URL = `https://wa.me/${SUPPORT_WA}?text=${encodeURIComponent(
  "Olá, li o FAQ da RAF Direct e gostaria de conversar com um orientador."
)}`;

/**
 * FAQ pública em /faq.
 * Cada FAQ segue as políticas de conformidade das páginas máscara para
 * anúncios: sem menção de substâncias, promessas, ou linguagem alarmista.
 *
 * Inclui JSON-LD Schema.org (FAQPage) para SEO — Google exibe rich snippets
 * nos resultados de busca.
 */

const FAQS = [
  {
    cat: "Sobre a plataforma",
    q: "O que é a RAF Direct?",
    a: "A RAF Direct é uma plataforma digital brasileira de orientação, informação e encaminhamento para famílias que buscam apoio em qualidade de vida, saúde mental e bem-estar. Conectamos famílias a unidades e profissionais credenciados, com processo humano, transparente e respeitoso.",
  },
  {
    cat: "Sobre a plataforma",
    q: "A RAF Direct é uma clínica?",
    a: "Não. A RAF Direct é uma plataforma de orientação e informação. Não realizamos diagnóstico, prescrição ou tratamento. Os serviços clínicos são prestados por unidades e profissionais credenciados, sob a responsabilidade técnica exclusiva destes, com seus respectivos registros junto aos conselhos profissionais (CRM, CRP, COREN, entre outros).",
  },
  {
    cat: "Sobre a plataforma",
    q: "Quem está por trás da RAF Direct?",
    a: "A operadora é a RAF - Rede de Apoio aos Familiares LTDA, CNPJ 61.281.627/0001-52, sediada em São Roque/SP. Dados institucionais completos, contatos oficiais, Política de Privacidade e Termos de Uso estão disponíveis na página /compliance.",
  },
  {
    cat: "Como começar",
    q: "Como faço para conversar com um orientador?",
    a: "Você pode falar com um orientador humano pelo WhatsApp (atendimento 24 horas, todos os dias) ou preencher um formulário simples no site. O primeiro contato é sempre acolhedor, confidencial e sem compromisso.",
  },
  {
    cat: "Como começar",
    q: "Preciso pagar para conversar com um orientador?",
    a: "Não. O contato inicial e a orientação para famílias são gratuitos. Nossa remuneração vem das unidades e profissionais credenciados quando você opta por seguir com algum atendimento.",
  },
  {
    cat: "Como começar",
    q: "Preciso me cadastrar para receber orientação?",
    a: "Você pode iniciar pelo WhatsApp sem se cadastrar. Para acompanhar um caso pela plataforma, receber propostas ou usar o marketplace de profissionais, é necessário criar uma conta rápida com e-mail e telefone.",
  },
  {
    cat: "Privacidade e segurança",
    q: "As informações que compartilho são confidenciais?",
    a: "Sim. Levamos privacidade a sério. Utilizamos criptografia em trânsito, controle de acesso por perfil e auditoria de ações sensíveis. Estamos em conformidade com a LGPD (Lei 13.709/2018). Detalhes em /politica-de-privacidade.",
  },
  {
    cat: "Privacidade e segurança",
    q: "Meus dados são vendidos ou compartilhados?",
    a: "Não vendemos dados. Compartilhamos informações apenas com unidades ou profissionais credenciados que você escolher seguir, e com fornecedores essenciais de tecnologia (hospedagem, e-mail, pagamentos). Sempre com bases legais previstas na LGPD.",
  },
  {
    cat: "Privacidade e segurança",
    q: "Posso pedir para excluir meus dados?",
    a: "Sim. A LGPD garante seu direito à eliminação de dados tratados com base no consentimento. Envie um e-mail para contato@rafdirect.com.br e responderemos em até 15 dias.",
  },
  {
    cat: "Rede credenciada",
    q: "Como as unidades e profissionais são selecionados?",
    a: "Fazemos curadoria documental básica no credenciamento (regularidade cadastral, alvarás e registros profissionais válidos) e monitoramos recorrentemente a regularidade. Cada parceiro é responsável técnico exclusivo pelos serviços que presta.",
  },
  {
    cat: "Rede credenciada",
    q: "Quem é o responsável pelo atendimento clínico?",
    a: "A responsabilidade técnica é sempre da unidade ou profissional credenciado que efetivamente prestar o atendimento. A RAF Direct atua exclusivamente na orientação inicial e no encaminhamento.",
  },
  {
    cat: "Rede credenciada",
    q: "Como faço para credenciar minha clínica ou meu consultório?",
    a: "Unidades podem se cadastrar na página /seja-parceiro. Profissionais em /seja-profissional. O credenciamento passa por aceite de termos e por validação documental antes da publicação no marketplace.",
  },
  {
    cat: "Custos e pagamentos",
    q: "A família paga alguma coisa para a RAF Direct?",
    a: "Não há cobrança para a família pelo atendimento orientativo. Serviços prestados por unidades ou profissionais credenciados podem ter custos, sempre informados com transparência antes de qualquer contratação.",
  },
  {
    cat: "Custos e pagamentos",
    q: "Como funcionam os pagamentos na plataforma?",
    a: "Pagamentos são processados por gateway certificado (Stripe), com opções de cartão de crédito e PIX (quando disponível). Não armazenamos números de cartão. Todos os pagamentos são registrados com comprovantes.",
  },
  {
    cat: "Situações urgentes",
    q: "E se for uma situação urgente?",
    a: "Em situações que envolvem risco imediato à vida, procure sempre os serviços públicos de emergência: SAMU 192, Bombeiros 193 ou pronto-socorro mais próximo. A RAF Direct oferece orientação, mas não substitui atendimento de urgência.",
  },
  {
    cat: "Situações urgentes",
    q: "Vocês atendem em qualquer horário?",
    a: "Sim, nosso canal de orientação por WhatsApp está disponível 24 horas por dia, todos os dias, com atendimento humano. Nos horários de menor movimento pode haver breve espera; nunca fica sem resposta.",
  },
];

const CATEGORIES = Array.from(new Set(FAQS.map((f) => f.cat)));

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`faq-item-${idx}`}
        className="w-full flex items-center justify-between gap-4 py-4 text-left"
      >
        <span className="font-semibold text-slate-900 pr-2">{q}</span>
        <ChevronDown
          className={`h-5 w-5 text-slate-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <p className="pb-5 pr-8 text-slate-700 leading-relaxed text-[15px]">{a}</p>}
    </div>
  );
};

export default function FAQ() {
  usePageTitle(
    "Perguntas frequentes",
    "Dúvidas comuns de famílias sobre a RAF Direct: como funciona, custos, privacidade, rede credenciada e situações urgentes. Respostas acolhedoras e claras."
  );
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQS;
    return FAQS.filter((f) => (f.q + " " + f.a).toLowerCase().includes(q));
  }, [query]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((f) => {
      (g[f.cat] = g[f.cat] || []).push(f);
    });
    return g;
  }, [filtered]);

  // JSON-LD Schema.org — rich snippets no Google
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);

  return (
    <div className="bg-white" data-testid="faq-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-[#F5F8FF] to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600">
            <HelpCircle className="h-3.5 w-3.5 text-[#0B2B62]" />
            Perguntas frequentes
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-[#0B2B62] leading-tight">
            Dúvidas comuns, respondidas com carinho
          </h1>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
            Reunimos aqui as perguntas mais frequentes de famílias que chegam até nós. Se não encontrar
            sua dúvida, fale com um orientador — atendimento humano, 24 horas.
          </p>

          {/* Busca */}
          <div className="mt-8 max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por palavra-chave (ex.: cadastro, pagamento, privacidade)"
                data-testid="faq-search"
                className="w-full rounded-xl border border-slate-300 bg-white pl-11 pr-4 py-3 text-slate-800 shadow-sm focus:outline-none focus:border-[#0B2B62] focus:ring-2 focus:ring-[#0B2B62]/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Lista de FAQs por categoria */}
      <section className="py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {Object.keys(grouped).length === 0 && (
            <div className="text-center text-slate-500 py-10">
              Nenhuma pergunta encontrada para "<strong>{query}</strong>". Tente outra palavra ou
              fale com um orientador.
            </div>
          )}

          {CATEGORIES.map((cat) => {
            const items = grouped[cat];
            if (!items || items.length === 0) return null;
            return (
              <div key={cat} className="mb-10" data-testid={`faq-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                  {cat}
                </div>
                <div className="mt-3 rounded-2xl border border-slate-200 bg-white px-5">
                  {items.map((f) => (
                    <FaqItem key={f.q} q={f.q} a={f.a} idx={FAQS.indexOf(f)} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA final */}
      <section className="pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="rounded-2xl bg-[#0B2B62] text-white p-8 sm:p-10 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold">Não encontrou sua resposta?</h2>
            <p className="mt-2 text-white/85">
              Um orientador humano está disponível para conversar quando você quiser.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="faq-cta-wa"
                onClick={() => trackWhatsApp("faq_bottom")}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white text-[#0B2B62] px-6 py-3 font-semibold hover:bg-slate-100 transition"
              >
                <MessageCircle className="h-5 w-5" />
                Fale com um Orientador
              </a>
              <Link
                to="/atendimento"
                data-testid="faq-cta-atendimento"
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/40 px-6 py-3 font-semibold hover:bg-white/10 transition"
              >
                Ver página de atendimento
              </Link>
            </div>
          </div>

          <div className="mt-8 text-xs text-slate-500 text-center leading-relaxed">
            Última revisão: fevereiro de 2026. Este conteúdo tem caráter informativo e não substitui a
            orientação profissional individualizada. Em urgências, procure SAMU 192.
          </div>
        </div>
      </section>
    </div>
  );
}
